import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileSpreadsheet, Plus, Trash2, Download, PieChart } from "lucide-react";
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const CATEGORIES = [
  { name: "Office Supplies", color: "#ffcc00" },
  { name: "Software & Tools", color: "#121212" },
  { name: "Marketing", color: "#4ade80" },
  { name: "Travel", color: "#60a5fa" },
  { name: "Professional Services", color: "#f472b6" },
  { name: "Equipment", color: "#a78bfa" },
  { name: "Utilities", color: "#fb923c" },
  { name: "Other", color: "#94a3b8" }
];

export default function ExpenseTracker() {
  const [expenses, setExpenses] = useState([
    { id: 1, description: "Adobe Creative Cloud", category: "Software & Tools", amount: 54.99, date: new Date().toISOString().split('T')[0] }
  ]);

  const addExpense = () => setExpenses([...expenses, { id: Date.now(), description: "", category: "Other", amount: 0, date: new Date().toISOString().split('T')[0] }]);
  const removeExpense = (id) => setExpenses(expenses.filter(e => e.id !== id));
  const updateExpense = (id, field, value) => setExpenses(expenses.map(e => e.id === id ? { ...e, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : e));

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);
  const byCategory = CATEGORIES.map(cat => ({
    name: cat.name,
    value: expenses.filter(e => e.category === cat.name).reduce((sum, e) => sum + e.amount, 0),
    color: cat.color
  })).filter(c => c.value > 0);

  const exportCSV = () => {
    const headers = "Date,Description,Category,Amount\n";
    const rows = expenses.map(e => `${e.date},"${e.description}",${e.category},${e.amount}`).join("\n");
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'expenses.csv';
    link.click();
  };

  return (
    <div className="min-h-screen" style={{ background: '#fffdf5' }}>
      <nav className="border-b border-[#e5e5e5] bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#666] hover:text-[#121212]">
            <ArrowLeft className="w-4 h-4" /> Back to FinAI
          </Link>
          <Button onClick={exportCSV} variant="outline" className="rounded-full">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#ffcc00]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileSpreadsheet className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl font-bold text-[#121212] mb-4">Expense Tracker</h1>
          <p className="text-lg text-[#666]">Track and categorize your business expenses—free forever.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-2 border-[#e5e5e5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Expenses</CardTitle>
                <Button onClick={addExpense} size="sm" className="rounded-full bg-[#121212]">
                  <Plus className="w-4 h-4 mr-2" /> Add Expense
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {expenses.map((expense) => (
                  <div key={expense.id} className="grid grid-cols-12 gap-2 items-center p-3 bg-[#faf8f0] rounded-xl">
                    <Input className="col-span-3" type="date" value={expense.date} onChange={e => updateExpense(expense.id, 'date', e.target.value)} />
                    <Input className="col-span-4" placeholder="Description" value={expense.description} onChange={e => updateExpense(expense.id, 'description', e.target.value)} />
                    <Select value={expense.category} onValueChange={v => updateExpense(expense.id, 'category', v)}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="col-span-1 relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#666]">$</span>
                      <Input className="pl-6" type="number" value={expense.amount} onChange={e => updateExpense(expense.id, 'amount', e.target.value)} />
                    </div>
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeExpense(expense.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="border-2 border-[#ffcc00]">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-[#666] mb-2">Total Expenses</p>
                <p className="text-4xl font-bold text-[#121212]">${total.toFixed(2)}</p>
                <p className="text-sm text-[#666] mt-2">{expenses.length} transactions</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#e5e5e5]">
              <CardHeader><CardTitle className="text-base">By Category</CardTitle></CardHeader>
              <CardContent>
                {byCategory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <RechartsPie>
                      <Pie data={byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                        {byCategory.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-[#666]">
                    <PieChart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Add expenses to see breakdown</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="text-center pt-4">
              <p className="text-[#666] text-sm mb-4">Want automatic categorization?</p>
              <Link to="/register">
                <Button className="rounded-full bg-[#ffcc00] text-[#121212] hover:bg-[#ffe566]">
                  Try FinAI Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
